/**
 * INFINITY LIFE 2.0 — IndexedDB Offline Persistence & Sync Queue
 */

const DB_NAME = 'infinity_life_db';
const DB_VERSION = 1;

let dbPromise = null;

export function openDB() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (e) => {
      const db = e.target.result;

      // 1. Cached Tasks Store
      if (!db.objectStoreNames.contains('cached_tasks')) {
        const taskStore = db.createObjectStore('cached_tasks', { keyPath: 'id' });
        taskStore.createIndex('due_date', 'due_date', { unique: false });
        taskStore.createIndex('status', 'status', { unique: false });
      }

      // 2. Offline Sync Queue Store
      if (!db.objectStoreNames.contains('sync_queue')) {
        const queueStore = db.createObjectStore('sync_queue', { keyPath: 'temp_id' });
        queueStore.createIndex('created_at', 'created_at', { unique: false });
      }
    };

    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => reject(e.target.error);
  });

  return dbPromise;
}

/**
 * Cache tasks from API response into IndexedDB
 */
export async function cacheTasks(tasks) {
  try {
    const db = await openDB();
    const tx = db.transaction('cached_tasks', 'readwrite');
    const store = tx.objectStore('cached_tasks');
    store.clear();
    for (const task of tasks) {
      store.put(task);
    }
  } catch (err) {
    console.warn('Failed to cache tasks in IndexedDB:', err);
  }
}

/**
 * Get all cached tasks from IndexedDB
 */
export async function getCachedTasks() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('cached_tasks', 'readonly');
      const store = tx.objectStore('cached_tasks');
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return [];
  }
}

/**
 * Enqueue an operation made while offline
 */
export async function enqueueOfflineOperation(type, data, id = null) {
  try {
    const db = await openDB();
    const tempId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
    const op = {
      temp_id: tempId,
      id: id,
      type: type, // 'create' | 'update' | 'toggle' | 'delete'
      data: data,
      created_at: new Date().toISOString(),
    };

    return new Promise((resolve, reject) => {
      const tx = db.transaction('sync_queue', 'readwrite');
      const store = tx.objectStore('sync_queue');
      const req = store.put(op);
      req.onsuccess = () => resolve(op);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.error('Failed to enqueue offline operation:', err);
    throw err;
  }
}

/**
 * Fetch all pending offline operations
 */
export async function getPendingOperations() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('sync_queue', 'readonly');
      const store = tx.objectStore('sync_queue');
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return [];
  }
}

/**
 * Remove processed operations from sync queue
 */
export async function clearProcessedOperations(tempIds) {
  try {
    const db = await openDB();
    const tx = db.transaction('sync_queue', 'readwrite');
    const store = tx.objectStore('sync_queue');
    for (const tempId of tempIds) {
      store.delete(tempId);
    }
  } catch (err) {
    console.warn('Failed to clear processed operations:', err);
  }
}
